import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createPollEvent } from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { getParticipantDisplayName } from '../../base/participants/functions';
import { useBoundSelector } from '../../base/util/hooks';
import { registerVote, removePoll, setVoteChanging } from '../actions';
import { getPoll } from '../functions';
/**
 * Higher Order Component taking in a concrete PollAnswer component and
 * augmenting it with state/behavior common to both web and native implementations.
 *
 * @param {React.AbstractComponent} Component - The concrete component.
 * @returns {React.AbstractComponent}
 */
const AbstractPollAnswer = (Component) => (props) => {
    const { pollId, setCreateMode } = props;
    const { conference } = useSelector((state) => state['features/base/conference']);
    const poll = useSelector(getPoll(pollId));
    const { answers, lastVote, question, senderId } = poll;
    const [checkBoxStates, setCheckBoxState] = useState(() => {
        if (lastVote !== null) {
            return [...lastVote];
        }
        return new Array(answers.length).fill(false);
    });
    const participantName = useBoundSelector(getParticipantDisplayName, senderId);
    const setCheckbox = useCallback((index, state) => {
        const newCheckBoxStates = [...checkBoxStates];
        newCheckBoxStates[index] = state;
        setCheckBoxState(newCheckBoxStates);
        sendAnalytics(createPollEvent('vote.checked'));
    }, [checkBoxStates]);
    const selectSingleAnswer = useCallback((selectedIndex) => {
        const newStates = new Array(answers.length).fill(false);
        newStates[selectedIndex] = true;
        setCheckBoxState(newStates);
        sendAnalytics(createPollEvent('vote.checked'));
    }, [answers.length]);
    const dispatch = useDispatch();
    const submitAnswer = useCallback(() => {
        conference?.getPolls().answerPoll(pollId, checkBoxStates);
        sendAnalytics(createPollEvent('vote.sent'));
        dispatch(registerVote(pollId, checkBoxStates));
        return false;
    }, [pollId, checkBoxStates, conference]);
    const sendPoll = useCallback(() => {
        conference?.getPolls().createPoll(pollId, question, answers);
        dispatch(removePoll(poll));
    }, [conference, question, answers]);
    const skipAnswer = useCallback(() => {
        dispatch(registerVote(pollId, null));
        sendAnalytics(createPollEvent('vote.skipped'));
    }, [pollId]);
    const skipChangeVote = useCallback(() => {
        dispatch(setVoteChanging(pollId, false));
    }, [dispatch, pollId]);
    const { t } = useTranslation();
    return (React.createElement(Component, { checkBoxStates: checkBoxStates, creatorName: participantName, poll: poll, selectSingleAnswer: selectSingleAnswer, sendPoll: sendPoll, setCheckbox: setCheckbox, setCreateMode: setCreateMode, skipAnswer: skipAnswer, skipChangeVote: skipChangeVote, submitAnswer: submitAnswer, t: t }));
};
export default AbstractPollAnswer;
